/**
 * Facebook Pixel Utility for Alphatica E-commerce
 * Pixel ID: 2481678482350081
 * 
 * Eventos implementados:
 * - PageView: Automático en cada página
 * - ViewContent: Detalle de producto
 * - AddToCart: Al agregar al carrito
 * - InitiateCheckout: Al iniciar checkout
 * - Purchase: Compra completada
 * - Search: Búsquedas de productos
 * - AddToWishlist: Al agregar a favoritos (futuro)
 */

const FB_PIXEL_ID = '2481678482350081';

// Initialize Facebook Pixel
export const initFBPixel = () => {
  if (typeof window === 'undefined') return;
  
  // Avoid duplicate initialization
  if (window.fbq) return;

  // Facebook Pixel base code
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', FB_PIXEL_ID);
  window.fbq('track', 'PageView');
  
  console.log('[FB Pixel] Initialized with ID:', FB_PIXEL_ID);
};

// Helper to safely call fbq
const fbq = (...args) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...args);
  }
};

// Track PageView (call on route changes)
export const trackPageView = () => {
  fbq('track', 'PageView');
};

/**
 * Track ViewContent - When user views a product
 * @param {Object} product - Product data
 */
export const trackViewContent = (product) => {
  if (!product) return;
  
  fbq('track', 'ViewContent', {
    content_name: product.name,
    content_category: product.gender || 'Perfume',
    content_ids: [product.id || product._id],
    content_type: 'product',
    value: product.price,
    currency: 'MXN',
    // Custom parameters for detailed remarketing
    brand: product.inspired_by_brand || '',
    product_code: product.product_code || '',
    perfume_type: product.perfume_type || '',
    olfactory_family: Array.isArray(product.olfactory_family) 
      ? product.olfactory_family.join(',') 
      : product.olfactory_family || '',
    gender: product.gender || '',
    intensity: product.intensity || ''
  });
  
  console.log('[FB Pixel] ViewContent:', product.name);
};

/**
 * Track AddToCart - When user adds product to cart
 * @param {Object} product - Product data
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!product) return;
  
  fbq('track', 'AddToCart', {
    content_name: product.name,
    content_category: product.gender || 'Perfume',
    content_ids: [product.id || product._id],
    content_type: 'product',
    value: product.price * quantity,
    currency: 'MXN',
    num_items: quantity,
    // Custom parameters
    brand: product.inspired_by_brand || '',
    product_code: product.product_code || '',
    perfume_type: product.perfume_type || '',
    gender: product.gender || ''
  });
  
  console.log('[FB Pixel] AddToCart:', product.name, 'x', quantity);
};

/**
 * Track InitiateCheckout - When user starts checkout
 * @param {Array} items - Cart items
 * @param {Object} totals - Cart totals
 */
export const trackInitiateCheckout = (items, totals) => {
  if (!items || items.length === 0) return;
  
  const contentIds = items.map(item => item.product_id || item.id);
  const contents = items.map(item => ({
    id: item.product_id || item.id,
    quantity: item.quantity,
    item_price: item.price
  }));
  
  fbq('track', 'InitiateCheckout', {
    content_ids: contentIds,
    contents: contents,
    content_type: 'product',
    num_items: items.reduce((sum, item) => sum + item.quantity, 0),
    value: totals?.total || totals?.subtotal || 0,
    currency: 'MXN'
  });
  
  console.log('[FB Pixel] InitiateCheckout:', items.length, 'items');
};

/**
 * Track AddPaymentInfo - When user starts entering payment details
 * @param {Object} orderData - Order information
 */
export const trackAddPaymentInfo = (orderData = {}) => {
  fbq('track', 'AddPaymentInfo', {
    content_category: 'Checkout',
    content_ids: orderData.contentIds || [],
    contents: orderData.contents || [],
    currency: 'MXN',
    value: orderData.value || 0,
    // Custom parameters for detailed remarketing
    order_id: orderData.orderId || '',
    payment_method_type: orderData.paymentMethodType || 'card',
    num_items: orderData.numItems || 0
  });
  
  console.log('[FB Pixel] AddPaymentInfo:', orderData.paymentMethodType || 'card');
};

/**
 * Track Purchase - When purchase is completed
 * @param {Object} orderData - Order details
 */
export const trackPurchase = (orderData) => {
  if (!orderData) return;
  
  const { items, totals, orderId, orderNumber } = orderData;
  
  const contentIds = items?.map(item => item.product_id || item.id) || [];
  const contents = items?.map(item => ({
    id: item.product_id || item.id,
    quantity: item.quantity,
    item_price: item.price
  })) || [];
  
  fbq('track', 'Purchase', {
    content_ids: contentIds,
    contents: contents,
    content_type: 'product',
    num_items: items?.reduce((sum, item) => sum + item.quantity, 0) || 0,
    value: totals?.total || 0,
    currency: 'MXN',
    // Order identifiers for deduplication
    order_id: orderId || orderNumber || '',
    // Custom parameters
    payment_method: orderData.payment_method || '',
    shipping_method: orderData.shipping_method || '',
    coupon_used: orderData.coupon_code || ''
  });
  
  console.log('[FB Pixel] Purchase:', orderData.orderNumber || orderId);
};

/**
 * Track Search - When user searches for products
 * @param {string} searchQuery - Search term
 * @param {Array} results - Search results (optional)
 */
export const trackSearch = (searchQuery, results = []) => {
  if (!searchQuery) return;
  
  fbq('track', 'Search', {
    search_string: searchQuery,
    content_category: 'Perfumes',
    content_ids: results.slice(0, 10).map(p => p.id || p._id),
    num_items: results.length,
    currency: 'MXN'
  });
  
  console.log('[FB Pixel] Search:', searchQuery, '→', results.length, 'results');
};

/**
 * Track AddToWishlist - When user adds to favorites
 * @param {Object} product - Product data
 */
export const trackAddToWishlist = (product) => {
  if (!product) return;
  
  fbq('track', 'AddToWishlist', {
    content_name: product.name,
    content_category: product.gender || 'Perfume',
    content_ids: [product.id || product._id],
    content_type: 'product',
    value: product.price,
    currency: 'MXN'
  });
  
  console.log('[FB Pixel] AddToWishlist:', product.name);
};

/**
 * Track Lead - When user submits contact form
 * @param {Object} leadData - Lead information
 */
export const trackLead = (leadData = {}) => {
  fbq('track', 'Lead', {
    content_name: leadData.source || 'Contact Form',
    content_category: leadData.category || 'General',
    currency: 'MXN',
    value: leadData.value || 0
  });
  
  console.log('[FB Pixel] Lead captured');
};

/**
 * Track CompleteRegistration - When user registers
 * @param {Object} userData - User data (no PII)
 */
export const trackCompleteRegistration = (userData = {}) => {
  fbq('track', 'CompleteRegistration', {
    content_name: 'User Registration',
    status: 'success',
    currency: 'MXN',
    value: userData.value || 0
  });
  
  console.log('[FB Pixel] CompleteRegistration');
};

/**
 * Custom event for specific remarketing audiences
 * @param {string} eventName - Custom event name
 * @param {Object} params - Event parameters
 */
export const trackCustomEvent = (eventName, params = {}) => {
  fbq('trackCustom', eventName, {
    ...params,
    currency: 'MXN'
  });
  
  console.log('[FB Pixel] Custom:', eventName, params);
};

// Export pixel ID for reference
export const PIXEL_ID = FB_PIXEL_ID;

export default {
  init: initFBPixel,
  pageView: trackPageView,
  viewContent: trackViewContent,
  addToCart: trackAddToCart,
  initiateCheckout: trackInitiateCheckout,
  addPaymentInfo: trackAddPaymentInfo,
  purchase: trackPurchase,
  search: trackSearch,
  addToWishlist: trackAddToWishlist,
  lead: trackLead,
  completeRegistration: trackCompleteRegistration,
  custom: trackCustomEvent
};
